import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, Check, Loader2, ShieldCheck, Vault, Scan } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  sovereign_property_scan: Scan,
  heritage_diligence_vault: Vault,
  ai_valuation_shield_pro: ShieldCheck,
};

export default function Pricing() {
  const [products, setProducts] = useState([]);
  const [law, setLaw] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => {
      setProducts(r.data.products);
      setLaw(r.data.lineage_law);
    }).catch(() => toast.error('Pricing offline.'));
  }, []);

  const buy = async (productKey) => {
    setLoadingKey(productKey);
    try {
      const token = localStorage.getItem('sov_token');
      const { data } = await axios.post(
        `${API}/checkout/session`,
        { product_key: productKey, origin_url: window.location.origin },
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true }
      );
      window.location.href = data.url;
    } catch (err) {
      toast.error('Checkout failed. Retry.');
      setLoadingKey(null);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-16">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Sovereign Pricing</div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Pay once.<br />
            <span className="font-display italic text-[#D4AF37]">Cross the corridor.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            Three sovereign products. Every dollar splits under the 75/15/10 Lineage Law.
            No subscriptions hidden in fine print. No surprise renewals.
          </p>
        </div>
      </div>

      {law && (
        <div className="grid grid-cols-3 gap-6 border-t border-b border-[#D4AF37]/15 py-8 mb-16">
          {[
            { l: 'Admiral', v: law.admiral_pct, n: 'Mercy' },
            { l: 'Operations', v: law.operations_pct, n: 'Vessel' },
            { l: 'CyberKids', v: law.cyberkids_pct, n: 'Legacy Fund' },
          ].map((s) => (
            <div key={s.l}>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">{s.l}</div>
              <div className="font-display text-4xl text-[#F5F5F5]">{s.v}%</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-2">{s.n}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {products.map((p, idx) => {
          const Icon = ICONS[p.key] || ShieldCheck;
          const featured = idx === 1;
          return (
            <div
              key={p.key}
              data-testid={`product-${p.key}`}
              className={`sov-border bg-[#0A0A0A] p-8 flex flex-col ${featured ? 'border-[#D4AF37]/60' : ''}`}
            >
              <div className="flex items-center justify-between mb-6">
                <Icon size={22} className="text-[#D4AF37]" strokeWidth={1} />
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{p.tier}</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-light tracking-tight mb-2">{p.name}</h3>
              <p className="text-sm text-[#A3A3A3] font-light leading-relaxed mb-8 min-h-[40px]">{p.tagline}</p>
              <div className="font-display text-5xl text-[#D4AF37] mb-2">${p.display_price_usd.toFixed(0)}</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-8">USD · One-time</div>
              <ul className="space-y-3 mb-10 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="text-sm font-light text-[#F5F5F5] flex gap-3 items-start">
                    <Check size={14} className="text-[#D4AF37] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => buy(p.key)}
                disabled={loadingKey === p.key}
                data-testid={`buy-${p.key}`}
                className="btn-sovereign w-full"
              >
                {loadingKey === p.key
                  ? <><Loader2 size={14} className="animate-spin" /> <span>Securing</span></>
                  : <><span>Acquire</span> <ArrowUpRight size={14} strokeWidth={1.5} /></>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
